import React, {useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import {ADMINISTERED_BY, administeredByLabel} from '../../../Profiles/ProfileTabs/MedicalTab/medicineAtSchoolRules';
import {useGetDosageUnitsQuery, useGetMedicineLookupsQuery} from '../../../../../api/services/medicinesService';
import {
    activeMedicines,
    amountQuestionLabel,
    EXPIRY_DAY_LABEL,
    expiryDays,
    findMedicineNameClash,
    matchesMedicineSearch,
    MEDICINE_CLASH_INSTRUCTION,
    MEDICINE_DIRECTIONS_MAX,
    MEDICINE_NAME_MAX,
    medicineClashHeading,
    medicineSearchOptions,
    medicineStockErrors,
    MONTH_NAMES,
    normaliseMedicineName,
    unitName
} from '../../../../../utility/medicines';

/* eslint-disable react/prop-types */

// The Medicine at School question. A parent enters one entry per box. When the form is accepted,
// each entry is added to the child's Medicine at School as a box (AD-2581).

const blankEntry = () => ({
    medicineLookupId: null,
    medicineName: '',
    brandName: '',
    notListed: false,
    directions: '',
    dosageUnit: '',
    // AD-2714: 'Staff', 'SelfAdministered' or blank; not required.
    administeredBy: '',
    amount: '',
    expiry: '',
    expiryDay: ''
});

const sameMedicine = entry => ({
    ...blankEntry(),
    medicineLookupId: entry.medicineLookupId,
    medicineName: entry.medicineName,
    brandName: entry.brandName,
    notListed: entry.notListed,
    directions: entry.directions,
    dosageUnit: entry.dosageUnit,
    administeredBy: entry.administeredBy ?? ''
});

const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;
const YEARS = Array.from({length: 11}, (_, i) => thisYear + i);

const MedicineStock = (
    {
        onChange,
        componentId,
        value,
        label,
        isEditMode,
        readonly,
        submitMode,
        previewMode,
        placeholderValues,
        error
    }) => {
    // A medicine the school has made inactive is not offered to parents. A parent who types its name makes it
    // active again when the form is accepted, and the school is told.
    const {data: everyMedicine = []} = useGetMedicineLookupsQuery(undefined, {skip: isEditMode});
    const medicines = activeMedicines(everyMedicine);
    const {data: units = []} = useGetDosageUnitsQuery();
    const [clash, setClash] = useState(null);
    // The medicine each box's directions were typed for, kept while the search box is cleared.
    const directionsFor = useRef({});
    // Once a parent has pressed Submit with a box incomplete, each box shows what is still missing.
    const [checked, setChecked] = useState(false);
    useEffect(() => {
        if (error) {
            setChecked(true);
        }
    }, [error]);

    const schoolName = placeholderValues?.schoolName || 'the school';
    const forename = placeholderValues?.studentForename || 'Your child';
    const entries = Array.isArray(value) && value.length > 0 ? value : [blankEntry()];
    const options = medicineSearchOptions(medicines);
    const boxErrors = checked ? medicineStockErrors(entries) : [];

    if (isEditMode) {
        return (
            <Stack gap={2} data-testid={`medicine-stock-settings-${componentId}`}>
                <TextField
                    fullWidth
                    label="Question"
                    value={label ?? ''}
                    data-testid={`medicine-stock-label-${componentId}`}
                    onChange={(event) => onChange({id: componentId, label: event.target.value})}
                />
                <Typography variant="body2" color="text.secondary">
                    The parent enters each box separately: the medicine, its brand, the directions, who
                    gives it, the dosage unit, the amount in the box and the expiry month. When the form is accepted,
                    each box is added to the child&apos;s Medicine at School. With approval stages, the boxes
                    are added when the last stage is approved.
                </Typography>
            </Stack>
        );
    }

    // A school sees the parent's screen in Preview, as on any other question.
    if (readonly || (!submitMode && !previewMode)) {
        const shown = Array.isArray(value) ? value : [];
        return (
            <Box data-testid={`medicine-stock-${componentId}`}>
                <Typography variant="body1" sx={{mb: 1}}>{label || 'Medicine at School'}</Typography>
                {shown.length === 0
                    ? <Typography variant="body2" color="text.secondary">No boxes entered.</Typography>
                    : (
                        <Box sx={{overflowX: 'auto'}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Medicine</TableCell>
                                        <TableCell>Brand name</TableCell>
                                        <TableCell>Directions</TableCell>
                                        <TableCell>Administered by</TableCell>
                                        <TableCell>Dosage unit</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Expiry</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shown.map((entry, i) => {
                                        const [year, month] = (entry.expiry ?? '').split('-').map(Number);
                                        return (
                                            <TableRow key={i}>
                                                <TableCell>{entry.medicineName}</TableCell>
                                                <TableCell>{entry.brandName}</TableCell>
                                                <TableCell>{entry.directions}</TableCell>
                                                <TableCell>{administeredByLabel(entry.administeredBy)}</TableCell>
                                                <TableCell>{unitName(entry.dosageUnit, units)}</TableCell>
                                                <TableCell>{entry.amount}</TableCell>
                                                <TableCell>{month ? `${entry.expiryDay ? `${entry.expiryDay} ` : ''}${MONTH_NAMES[month - 1]} ${year}` : ''}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
            </Box>
        );
    }

    const save = next => onChange({id: componentId, value: next});
    const update = (index, changes) => save(entries.map((entry, i) => (i === index ? {...entry, ...changes} : entry)));

    // A typed name already on the school's list is turned back with the agreed pop-up.
    const checkTyped = (index, typed) => {
        const found = findMedicineNameClash(medicines, typed);
        if (found) {
            setClash({index, typed});
        }
    };

    const backToTheList = () => {
        update(clash.index, {notListed: false, medicineName: '', brandName: '', medicineLookupId: null});
        setClash(null);
    };

    const choose = (index, option) => {
        // Olena, 8 October 2026: the directions belong to the medicine, so another medicine starts
        // them again. A brand of the same medicine keeps them, even after the search box is cleared.
        const entry = entries[index];
        const typedFor = entry.medicineLookupId ?? directionsFor.current[index];

        if (!option) {
            directionsFor.current[index] = typedFor;
            update(index, {medicineLookupId: null, medicineName: '', brandName: ''});
            return;
        }

        directionsFor.current[index] = option.medicineLookupId;
        update(index, {
            directions: option.medicineLookupId === typedFor ? entry.directions : '',
            medicineLookupId: option.medicineLookupId,
            medicineName: option.medicineName,
            brandName: option.brandName,
            typedBrand: false,
            dosageUnit: option.dosageUnit || entry.dosageUnit
        });
    };

    const brandsOf = entry => medicines.find(medicine => medicine.id === entry.medicineLookupId)?.brands ?? [];

    // A brand picked from the medicine's own brands brings its usual unit with it. The parent types the amount from
    // the box (Maria, 24 October 2026).
    const chooseBrand = (index, name) => {
        const entry = entries[index];
        const brand = brandsOf(entry).find(b => b.name === name);
        if (!brand) {
            update(index, {brandName: name ?? '', typedBrand: Boolean(name)});
            return;
        }

        update(index, {
            brandName: brand.name,
            typedBrand: false,
            dosageUnit: brand.dosageUnit || entry.dosageUnit
        });
    };

    // A typed brand that is one of this medicine's own brands, in other capitals or spacing, is taken
    // as that brand. Any other name already on the school's list is turned back with the pop-up.
    const settleBrand = index => {
        const entry = entries[index];
        const typed = entry.brandName ?? '';
        const own = brandsOf(entry).find(b => normaliseMedicineName(b.name) === normaliseMedicineName(typed));
        if (own) {
            if (own.name !== typed) {
                chooseBrand(index, own.name);
            }
            return;
        }

        checkTyped(index, typed);
    };

    return (
        <Box data-testid={`medicine-stock-${componentId}`}>
            <Typography variant="body1" sx={{mb: 2}}>{label || 'Medicine at School'}</Typography>
            <Stack gap={3}>
                {entries.map((entry, index) => {
                    const [year, month] = (entry.expiry ?? '').split('-');
                    // A brand the parent typed for a listed medicine keeps the medicine chosen in the search.
                    const selected = options.find(o =>
                            o.medicineLookupId === entry.medicineLookupId && o.brandName === (entry.brandName ?? '')) ??
                        options.find(o => o.medicineLookupId === entry.medicineLookupId && !o.brandName) ??
                        null;
                    const listedMedicine = !entry.notListed && selected != null;
                    const messages = boxErrors[index] ?? [];

                    return (
                        <Box
                            key={index}
                            data-testid={`medicine-stock-entry-${index}`}
                            sx={{border: 1, borderColor: 'divider', borderRadius: 1, p: 2}}
                        >
                            <Stack gap={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">Box {index + 1}</Typography>
                                    {entries.length > 1 && (
                                        <Button
                                            size="small"
                                            color="error"
                                            data-testid={`medicine-stock-remove-${index}`}
                                            onClick={() => save(entries.filter((_, i) => i !== index))}
                                        >
                                            Remove this box
                                        </Button>
                                    )}
                                </Stack>

                                {!entry.notListed && (
                                    <Autocomplete
                                        options={options}
                                        value={selected}
                                        getOptionLabel={option => option.label}
                                        isOptionEqualToValue={(a, b) => a.key === b.key}
                                        filterOptions={(all, state) => all.filter(o => matchesMedicineSearch(o, state.inputValue))}
                                        onChange={(_, option) => choose(index, option)}
                                        noOptionsText={`Not found. Tick "${forename}'s medicine is not on this list" below.`}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Search by medicine name or brand name"
                                                inputProps={{...params.inputProps, 'data-testid': `medicine-stock-search-${index}`}}
                                            />
                                        )}
                                    />
                                )}

                                {listedMedicine && (
                                    <Autocomplete
                                        freeSolo
                                        options={brandsOf(entry).map(brand => brand.name)}
                                        value={entry.brandName ?? ''}
                                        inputValue={entry.brandName ?? ''}
                                        onChange={(_, name) => chooseBrand(index, name)}
                                        onInputChange={(_, text, reason) => {
                                            if (reason === 'input') {
                                                update(index, {brandName: text, typedBrand: true});
                                            }
                                        }}
                                        onBlur={() => settleBrand(index)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Brand name"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    maxLength: MEDICINE_NAME_MAX,
                                                    'data-testid': `medicine-stock-new-brand-${index}`
                                                }}
                                            />
                                        )}
                                    />
                                )}

                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={Boolean(entry.notListed)}
                                            data-testid={`medicine-stock-not-listed-${index}`}
                                            onChange={(event) => update(index, {
                                                notListed: event.target.checked,
                                                medicineLookupId: null,
                                                medicineName: '',
                                                brandName: '',
                                                directions: ''
                                            })}
                                        />
                                    )}
                                    label={`${forename}'s medicine is not on this list`}
                                />

                                {entry.notListed && (
                                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                                        <TextField
                                            fullWidth
                                            label="Medicine name"
                                            value={entry.medicineName}
                                            inputProps={{maxLength: MEDICINE_NAME_MAX, 'data-testid': `medicine-stock-medicine-name-${index}`}}
                                            onChange={(event) => update(index, {medicineName: event.target.value})}
                                            onBlur={(event) => checkTyped(index, event.target.value)}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Brand name"
                                            value={entry.brandName}
                                            inputProps={{maxLength: MEDICINE_NAME_MAX, 'data-testid': `medicine-stock-brand-name-${index}`}}
                                            onChange={(event) => update(index, {brandName: event.target.value})}
                                            onBlur={(event) => checkTyped(index, event.target.value)}
                                        />
                                    </Stack>
                                )}

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Directions, as printed on the pharmacy label"
                                    value={entry.directions}
                                    inputProps={{maxLength: MEDICINE_DIRECTIONS_MAX, 'data-testid': `medicine-stock-directions-${index}`}}
                                    onChange={(event) => update(index, {directions: event.target.value})}
                                />

                                <TextField
                                    select
                                    fullWidth
                                    label="Administered by"
                                    value={entry.administeredBy ?? ''}
                                    inputProps={{'data-testid': `medicine-stock-administered-by-${index}`}}
                                    onChange={(event) => update(index, {administeredBy: event.target.value})}
                                >
                                    {ADMINISTERED_BY.map(choice => (
                                        <MenuItem key={choice.key} value={choice.key}>{choice.label}</MenuItem>
                                    ))}
                                </TextField>

                                <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Dosage unit"
                                        value={entry.dosageUnit ?? ''}
                                        inputProps={{'data-testid': `medicine-stock-unit-${index}`}}
                                        onChange={(event) => update(index, {dosageUnit: event.target.value})}
                                    >
                                        {units.map(unit => (
                                            <MenuItem key={unit.key} value={unit.key}>
                                                {unitName(unit.key, units)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={amountQuestionLabel(entry.dosageUnit, units)}
                                        value={entry.amount ?? ''}
                                        inputProps={{min: 1, step: 1, 'data-testid': `medicine-stock-amount-${index}`}}
                                        onChange={(event) => update(index, {
                                            amount: event.target.value === '' ? '' : Number(event.target.value)
                                        })}
                                    />
                                </Stack>

                                <TextField
                                    select
                                    fullWidth
                                    label={EXPIRY_DAY_LABEL}
                                    value={entry.expiryDay ?? ''}
                                    inputProps={{'data-testid': `medicine-stock-expiry-day-${index}`}}
                                    onChange={(event) => update(index, {expiryDay: event.target.value === '' ? '' : Number(event.target.value)})}
                                >
                                    <MenuItem value="">&nbsp;</MenuItem>
                                    {expiryDays(year, month).map(d => <MenuItem key={d} value={Number(d)}>{d}</MenuItem>)}
                                </TextField>
                                <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Expiry month, as printed on the box"
                                        value={month ?? ''}
                                        inputProps={{'data-testid': `medicine-stock-expiry-month-${index}`}}
                                        onChange={(event) => update(index, {expiry: `${year || thisYear}-${event.target.value}`, expiryDay: Number(entry.expiryDay) > expiryDays(year || thisYear, event.target.value).length ? '' : entry.expiryDay})}
                                    >
                                        {MONTH_NAMES.map((name, i) => (
                                            <MenuItem
                                                key={name}
                                                value={String(i + 1).padStart(2, '0')}
                                                disabled={Number(year) === thisYear && i + 1 < thisMonth}
                                            >
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Expiry year"
                                        value={year ?? ''}
                                        inputProps={{'data-testid': `medicine-stock-expiry-year-${index}`}}
                                        onChange={(event) => update(index, {expiry: `${event.target.value}-${month || '01'}`})}
                                    >
                                        {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                                    </TextField>
                                </Stack>

                                {messages.length > 0 && (
                                    <Box data-testid={`medicine-stock-errors-${index}`}>
                                        {messages.map(message => (
                                            <Typography key={message} variant="body2" color="error">{message}</Typography>
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    );
                })}

                <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                    <Button
                        variant="outlined"
                        data-testid="medicine-stock-add-same"
                        onClick={() => save([...entries, sameMedicine(entries[entries.length - 1])])}
                    >
                        Add another of the same medicine
                    </Button>
                    <Button
                        variant="outlined"
                        data-testid="medicine-stock-add-different"
                        onClick={() => save([...entries, blankEntry()])}
                    >
                        Add a different medicine
                    </Button>
                </Stack>
            </Stack>

            <AdmicityDialog
                open={Boolean(clash)}
                title=""
                handleClose={backToTheList}
                actions={[{label: 'Back to the list', onClick: backToTheList}]}
            >
                <Box data-testid="medicine-stock-clash">
                    <Typography fontWeight="bold" sx={{mb: 1}}>
                        {clash ? medicineClashHeading(clash.typed, schoolName) : ''}
                    </Typography>
                    <Typography>{MEDICINE_CLASH_INSTRUCTION}</Typography>
                </Box>
            </AdmicityDialog>
        </Box>
    );
};

export default MedicineStock;
